import { buildAppointmentIcs, IcsInput } from '@chiroup/core/functions/buildAppointmentIcs';
import locationService from '../services/location.service';

// The ICS builder itself now lives in `@chiroup/core` so the backend
// `.ics` endpoint and this app share one implementation. This module
// keeps the browser-side "trigger the calendar" behavior.
export type { IcsInput } from '@chiroup/core/functions/buildAppointmentIcs';

const isIos = (): boolean => {
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ in desktop mode reports as Macintosh.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

// Blob downloads are a silent no-op in the Capacitor Android WebView.
const isNativeAndroid = (): boolean =>
  ((
    window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }
  ).Capacitor?.isNativePlatform?.() ??
    false) &&
  /Android/i.test(navigator.userAgent);

// Opens the .ics URL. The API host isn't in the Capacitor `allowNavigation`
// allowlist, so this top-level navigation is handed off to the system
// browser (Safari on iOS, Chrome on Android), which handles the
// `text/calendar` response natively. The in-app webview navigation is
// cancelled by Capacitor, so the SPA stays where it is.
//
// We use `location.href` rather than `window.open`: by this point the token
// fetch has consumed the user-gesture, and WebKit blocks gesture-less popups
// — a location assignment has no such restriction.
const triggerCalendarUrl = (url: string): void => {
  window.location.href = url;
};

/**
 * Adds the appointment to the device calendar.
 *
 * iOS: the share-sheet / anchor-download approaches don't reach Calendar —
 * a shared file is treated as a generic document, and iOS ignores the
 * `download` attribute. Instead we fetch a short-lived signed token and open
 * the backend `.ics` endpoint (served as `text/calendar`), which makes
 * WebKit show the native "Add to Calendar" screen. The token is required
 * because an iframe/navigation can't carry the session bearer header.
 *
 * Desktop / mobile browser: the anchor+download pattern works reliably, so we
 * build the ICS client-side and download it (unchanged).
 *
 * Returns `true` when the action fired, or `false` when it may have been
 * silently swallowed (link mint failed, etc.) so callers can show a hint.
 */
export const downloadAppointmentIcs = async (input: IcsInput): Promise<boolean> => {
  if (isIos() || isNativeAndroid()) {
    try {
      const { token } = await locationService.getCalendarLink(input.appointmentId);

      if (!token) return false;

      const base = import.meta.env.VITE_HEALTHCOM_SCHEDULE_API as string;
      const url = `${base}/appointment/${encodeURIComponent(
        input.appointmentId
      )}/calendar.ics?token=${encodeURIComponent(token)}`;

      triggerCalendarUrl(url);
      return true;
    } catch {
      return false;
    }
  }

  // Desktop / mobile browser: the anchor+download pattern works reliably.
  const ics = buildAppointmentIcs(input);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const fileName = `appointment-${input.appointmentId}.ics`;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  setTimeout(() => URL.revokeObjectURL(url), 250);

  return true;
};
